import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api';
import { Wordmark } from '../../components/Logo';

// Public page reached by scanning the QR code on a printed document. It calls
// the public /verify endpoint, which re-checks the HMAC signature against the
// live record and (only when valid) returns the authoritative figures.

interface VerifiedDoc {
  type: string;
  typeLabel: string;
  number: string;
  issuedDate: string;
  total: string | null;
  currency: string;
  status: string | null;
  customerName: string;
  shopName: string;
  companyName: string;
  extra?: Record<string, string>;
}

interface VerifyResponse {
  verified: boolean;
  found: boolean;
  type: string;
  document?: VerifiedDoc;
}

function fmtMUR(v: string | null): string {
  if (v == null) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return n.toLocaleString('en-MU', {
    style: 'currency',
    currency: 'MUR',
    minimumFractionDigits: 2,
  });
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-MU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex justify-between gap-6 py-2 border-b border-rule last:border-0">
      <span className="text-ink-3 text-sm">{label}</span>
      <span className="text-ink text-sm font-medium text-right">{value}</span>
    </div>
  );
}

export function VerifyDocumentPage(): JSX.Element {
  const { docType = '', id = '' } = useParams();
  const [params] = useSearchParams();
  const sig = params.get('sig') ?? '';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['verify', docType, id, sig],
    queryFn: () => apiGet<VerifyResponse>(`/verify/${docType}/${id}`, { sig }),
    retry: false,
  });

  return (
    <div className="min-h-screen bg-paper flex flex-col items-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <Wordmark size="md" />
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-3 mt-4">
            Document verification
          </div>
        </div>

        {isLoading && (
          <div className="text-center text-ink-3 text-sm py-16">Checking document…</div>
        )}

        {isError && !isLoading && (
          <Banner tone="negative" title="Verification unavailable">
            We couldn’t reach the verification service. Please check your connection and try again.
          </Banner>
        )}

        {data && !isLoading && (
          <>
            {data.verified && data.document ? (
              <div className="space-y-5">
                <Banner tone="positive" title="Authentic document">
                  This document is genuine and has not been altered. Compare the details below with
                  the document in front of you — they must match exactly.
                </Banner>

                <div className="bg-surface rounded-lg border border-rule p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-ink-3">
                    {data.document.typeLabel}
                  </div>
                  <div className="kc-serif text-2xl text-ink mt-1 mb-3">{data.document.number}</div>
                  <Row label="Issued" value={fmtDate(data.document.issuedDate)} />
                  {data.document.total != null && (
                    <Row label="Total" value={fmtMUR(data.document.total)} />
                  )}
                  {data.document.status && <Row label="Status" value={data.document.status} />}
                  <Row label="Customer" value={data.document.customerName} />
                  <Row label="Shop" value={data.document.shopName} />
                  {Object.entries(data.document.extra ?? {}).map(([k, v]) => (
                    <Row key={k} label={k} value={v} />
                  ))}
                </div>

                <p className="text-xs text-ink-3 text-center leading-relaxed">
                  Issued by {data.document.companyName}. If any figure printed on your copy differs
                  from what is shown here, the printed document may have been tampered with.
                </p>
              </div>
            ) : data.found ? (
              <Banner tone="negative" title="Could not be verified">
                A document with this reference exists, but its security signature is invalid. This
                copy may have been altered. Please contact us to confirm the original.
              </Banner>
            ) : (
              <Banner tone="negative" title="Document not found">
                This reference does not exist in our records. The document may be fabricated. Please
                contact us if you received it from us.
              </Banner>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Banner({
  tone,
  title,
  children,
}: {
  tone: 'positive' | 'negative' | 'warning';
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  const palette = {
    positive: 'bg-positive-soft text-positive border-positive/20',
    negative: 'bg-negative-soft text-negative border-negative/20',
    warning: 'bg-warning-soft text-warning border-warning/20',
  }[tone];
  const icon = tone === 'positive' ? '✓' : '✕';
  return (
    <div className={`rounded-lg border px-5 py-4 ${palette}`}>
      <div className="flex items-center gap-2 font-semibold">
        <span className="text-lg leading-none">{icon}</span>
        <span>{title}</span>
      </div>
      <p className="text-sm mt-1.5 text-ink-2 leading-relaxed">{children}</p>
    </div>
  );
}
